/**
 * ConflictResolutionDialog — centered popup (replaces the right-side sheet,
 * which the user noted felt out-of-place for non-Push surfaces).
 *
 * Now does real work:
 *   1. Shows detected conflicts (crew double-booking, missing assignment, …)
 *   2. Generates 3-6 candidate fixes (same-crew/different-day; other crews)
 *   3. Runs each fix through `analyzeDrop` so each option shows its impact:
 *        • verdict pill (valid / warning / blocked)
 *        • utilization delta (+/- N%)
 *        • travel level (low / medium / high)
 *        • crew fit score
 *   4. One-click apply uses the same optimistic + Z-undo path as drag-to-book
 *
 * Architectural note: scheduling-owned fields (assignedCrew,
 * scheduledInstallDate) are mutated optimistically — no proposal queue.
 */
import { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAppData } from "@/data/AppDataContext";
import { useSchedulingConflicts } from "@/hooks/useSchedulingConflicts";
import { buildCapacityGrid } from "@/data/scheduling/capacityEngine";
import { analyzeDrop } from "@/data/scheduling/dropAnalyzer";
import { derivePreSpendGate, type PreSpendGate, type PreSpendGateName } from "@/data/preSpendGate";
import { isExternalManagedImport } from "@/data/import/migrationVisibilityGuard";
import { format, parseISO } from "date-fns";
import { AlertTriangle, ArrowRight, Check, ExternalLink, ShieldAlert, ClipboardCheck, DollarSign, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { toast as sonnerToast } from "sonner";
import { cn } from "@/lib/utils";

const GATE_ICON: Record<PreSpendGateName, typeof ShieldAlert> = {
  review: ClipboardCheck,
  rescission: Clock,
  financial: DollarSign,
};

/** Where to send the operator to clear each gate. Keeps them in flow. */
function gateCta(jobId: string, name: PreSpendGateName): { label: string; href: string } {
  switch (name) {
    case "review":     return { label: "Open Build Review", href: `/jobs/${jobId}#build-review` };
    case "financial":  return { label: "Resolve financial",  href: `/jobs/${jobId}#financial` };
    case "rescission": return { label: "View rescission",    href: `/jobs/${jobId}#timeline` };
  }
}

interface Props {
  open: boolean;
  onClose: () => void;
  jobId: string | null;
  /** Apply a suggested move (jobId → crewName + dateIso). Wired to PlanCockpit's bookJobToCell. */
  onApplyMove?: (jobId: string, crewName: string, dateIso: string) => void;
}

interface ScoredSuggestion {
  crewName: string;
  dateIso: string;
  label: string;
  sub: string;
  verdict: "valid" | "warning" | "blocked";
  headline: string;
  utilDelta: number;
  newUtilizationPct: number;
  travel: "low" | "med" | "high" | "unknown";
  fitScore: number | null;
  /** Used to rank: valid > warning > blocked, then by lower travel + higher fit. */
  rank: number;
}

export function ConflictResolutionDrawer({ open, onClose, jobId, onApplyMove }: Props) {
  const { jobs, issues, contractors, opportunities, credits, buildReviewEvents } = useAppData();
  const { getJobConflicts } = useSchedulingConflicts(jobs);

  const job = useMemo(() => jobs.find(j => j.id === jobId), [jobs, jobId]);
  const conflicts = jobId ? getJobConflicts(jobId) : [];

  // Pre-spend gate: when not cleared, capacity is irrelevant — readiness
  // is the real blocker, and we tell the operator that explicitly.
  const gate: PreSpendGate | null = useMemo(
    () => {
      if (!job) return null;
      if (isExternalManagedImport(job)) {
        return { cleared: true, checks: [], shortReason: "Externally managed import" };
      }
      return derivePreSpendGate(job);
    },
    [job],
  );

  const grid = useMemo(
    () => buildCapacityGrid({
      jobs, issues, contractors,
      contractorOpportunities: opportunities,
      credits, buildReviewEvents,
    }),
    [jobs, issues, contractors, opportunities, credits, buildReviewEvents],
  );

  /**
   * Build candidate fixes and score each via analyzeDrop. Two pools:
   *   • Same crew, different day  (least disruptive)
   *   • Other crews, first open day each
   * Then sort by verdict → travel → fit.
   */
  const suggestions: ScoredSuggestion[] = useMemo(() => {
    if (!job) return [];
    const out: ScoredSuggestion[] = [];
    const currentCrew = job.assignedCrew || job.assignedSubcontractor;
    const candidates: { crewName: string; dateIso: string; sub: string }[] = [];

    if (currentCrew) {
      const row = grid.rows.find(r => r.crewName === currentCrew);
      if (row) {
        for (const cell of row.cells) {
          if (cell.state === "open") {
            candidates.push({
              crewName: row.crewName,
              dateIso: cell.dateIso,
              sub: "Same crew, different day",
            });
          }
        }
      }
    }
    for (const row of grid.rows) {
      if (row.crewName === currentCrew) continue;
      // Skip synthetic placeholders — they have no real fit/travel context.
      if (row.crewName.startsWith("Open crew slot")) continue;
      const open = row.cells.find(c => c.state === "open");
      if (open) {
        candidates.push({
          crewName: row.crewName,
          dateIso: open.dateIso,
          sub: `${row.crewType === "Subcontractor" ? "Sub" : "Internal"} · was ${row.utilizationPct}% loaded`,
        });
      }
    }

    for (const c of candidates) {
      const a = analyzeDrop({
        jobId: job.id,
        targetCrewName: c.crewName,
        targetDateIso: c.dateIso,
        grid, jobs, contractors,
      });
      const travelRank = a.travel === "high" ? 3 : a.travel === "med" ? 2 : a.travel === "low" ? 1 : 0;
      const verdictRank = a.verdict === "valid" ? 0 : a.verdict === "warning" ? 1 : 2;
      const rank = verdictRank * 100 + travelRank * 10 - (a.fitScore ?? 0) / 100;
      out.push({
        crewName: c.crewName,
        dateIso: c.dateIso,
        label: `${c.crewName} · ${format(parseISO(c.dateIso), "EEE MMM d")}`,
        sub: c.sub,
        verdict: a.verdict,
        headline: a.headline,
        utilDelta: a.utilDelta,
        newUtilizationPct: a.newUtilizationPct,
        travel: a.travel,
        fitScore: a.fitScore,
        rank,
      });
    }

    out.sort((a, b) => a.rank - b.rank);
    return out.slice(0, 6);
  }, [job, grid, jobs, contractors]);

  // Capacity-vs-readiness diagnosis:
  //  • capacity exists when at least one suggestion was generated (open slots found)
  //  • all suggestions blocked + gate not cleared → "readiness, not capacity" state
  const capacityExists = suggestions.length > 0;
  const allBlocked = capacityExists && suggestions.every(s => s.verdict === "blocked");
  const readinessIsRoot = !!gate && !gate.cleared && allBlocked;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(96vw,900px)] max-w-[min(96vw,900px)] max-h-[85vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Resolve scheduling conflict
          </DialogTitle>
          <DialogDescription className="text-xs">
            {job
              ? <>Each fix below is scored against current capacity, travel, and crew fit. Click <span className="text-foreground font-medium">Apply</span> to move <span className="text-foreground font-medium">{job.customerName}</span>; press <kbd className="px-1 py-0.5 rounded bg-muted text-[10px]">Z</kbd> within 8s to undo.</>
              : "Select a conflict to resolve."}
          </DialogDescription>
        </DialogHeader>

        {!job ? (
          <p className="text-xs text-muted-foreground mt-4">No job selected.</p>
        ) : (
          <div className="mt-4 space-y-5">
            {/* ── Readiness gate panel ──────────────────────────────
                Surfaces upstream blockers BEFORE the operator hunts
                for a "valid" suggestion that doesn't exist. */}
            {gate && !gate.cleared && (
              <section className={cn(
                "rounded-md border px-3 py-2.5",
                readinessIsRoot
                  ? "border-warning/50 bg-warning/[0.06]"
                  : "border-border bg-muted/30",
              )}>
                <div className="flex items-start gap-2">
                  <ShieldAlert className={cn(
                    "h-4 w-4 mt-0.5 shrink-0",
                    readinessIsRoot ? "text-warning" : "text-muted-foreground",
                  )} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] font-semibold text-foreground">
                      {readinessIsRoot
                        ? "This job cannot be placed yet"
                        : "Heads up: readiness gates pending"}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {readinessIsRoot
                        ? "Crew capacity exists this week, but the job is not schedulable until upstream gates clear."
                        : "Some pre-spend gates are open. Suggestions below may be blocked even where capacity exists."}
                    </p>
                  </div>
                </div>
                <ul className="mt-2.5 space-y-1.5">
                  {gate.checks.filter(c => !c.cleared).map(c => {
                    const Icon = GATE_ICON[c.name];
                    const cta = gateCta(job.id, c.name);
                    return (
                      <li key={c.name} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded border border-border/60 bg-background px-2.5 py-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <p className="text-[11.5px] font-medium text-foreground truncate">{c.label}</p>
                            <p className="text-[10.5px] text-muted-foreground truncate">{c.detail}</p>
                          </div>
                        </div>
                        <Button asChild size="sm" variant="outline" className="h-6 text-[10.5px] px-2 shrink-0 self-start sm:self-auto whitespace-nowrap">
                          <Link to={cta.href} onClick={onClose}>
                            {cta.label}
                          </Link>
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {/* Detected conflicts */}
            {conflicts.length > 0 && (
              <section>
                <p className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground mb-2">
                  Detected ({conflicts.length})
                </p>
                <ul className="space-y-1.5">
                  {conflicts.map((c, i) => (
                    <li key={i} className="border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">
                      <p className="text-[12px] font-medium text-foreground">{c.label}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{c.detail}</p>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Suggested resolutions — suppressed entirely when readiness is
                the root cause. Listing "blocked" alternatives for a job that
                literally can't move yet is misleading: it implies the operator
                has scheduling options, when in fact the only valid action is
                to clear the upstream gate. Once the gate clears, this section
                reappears with real, applyable suggestions. */}
            {gate && !gate.cleared ? (
              <section>
                <p className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground mb-2">
                  Suggested fixes
                </p>
                <p className="text-[11px] text-muted-foreground py-3">
                  Suggestions are hidden until upstream gates clear. This job
                  isn't schedulable yet — resolve the readiness blockers above
                  and fixes will appear here automatically.
                </p>
              </section>
            ) : (
              <section>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground">
                    Suggested fixes ({suggestions.length})
                  </p>
                  <p className="text-[10px] text-muted-foreground/70">
                    Ranked by fit · travel · capacity
                  </p>
                </div>
                {suggestions.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground py-3">
                    No open slots this week. Try the next week or free capacity.
                  </p>
                ) : (
                  <ul className="space-y-1.5">
                    {suggestions.map((s, i) => (
                      <li key={i}>
                        <SuggestionRow
                          suggestion={s}
                          onApply={() => {
                            if (!onApplyMove) return;
                            // Re-check truth at commit time. The drawer may have been
                            // open while the job's gates regressed (payment failure,
                            // permit revoke) or another tab consumed the target slot.
                            // Snapshot-based "Apply" must never silently commit.
                            const liveJob = jobs.find(jj => jj.id === job.id);
                            if (!liveJob) {
                              sonnerToast.error("Job no longer exists", {
                                description: "Refresh and try again.",
                              });
                              onClose();
                              return;
                            }
                            const bypassLiveGate = isExternalManagedImport(liveJob);
                            const liveGate = derivePreSpendGate(liveJob);
                            if (!bypassLiveGate && !liveGate.cleared) {
                              sonnerToast.error("Job no longer eligible — resolve gates first", {
                                description: liveGate.shortReason ?? "A pre-spend gate regressed while this dialog was open.",
                                duration: 7000,
                              });
                              return;
                            }
                            const liveAnalysis = analyzeDrop({
                              jobId: liveJob.id,
                              targetCrewName: s.crewName,
                              targetDateIso: s.dateIso,
                              grid, jobs, contractors,
                            });
                            if (liveAnalysis.verdict === "blocked") {
                              sonnerToast.error("Slot is no longer available", {
                                description: liveAnalysis.headline,
                                duration: 7000,
                              });
                              return;
                            }
                            onApplyMove(job.id, s.crewName, s.dateIso);
                            onClose();
                          }}
                          disabled={!onApplyMove || s.verdict === "blocked"}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
            {/* Footer */}
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-border/60">
              <Link to={`/jobs/${job.id}`} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                <ExternalLink className="h-3 w-3" />
                Open full job detail
              </Link>
              <Button variant="ghost" size="sm" onClick={onClose} className="text-xs">
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SuggestionRow({
  suggestion: s, onApply, disabled,
}: {
  suggestion: ScoredSuggestion;
  onApply: () => void;
  disabled: boolean;
}) {
  const verdictBadge =
    s.verdict === "valid"   ? "bg-success/15 text-success border-success/30" :
    s.verdict === "warning" ? "bg-warning/15 text-warning border-warning/30" :
                              "bg-destructive/15 text-destructive border-destructive/30";
  const verdictLabel =
    s.verdict === "valid"   ? "Best fit" :
    s.verdict === "warning" ? "Caution"  :
                              "Blocked";

  return (
    <div className={cn(
      "border border-border/60 rounded-md px-3 py-2.5 transition-colors",
      s.verdict === "valid" && "border-success/40 bg-success/[0.03]",
      s.verdict === "warning" && "border-warning/30",
      s.verdict === "blocked" && "opacity-60",
    )}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[12px] font-medium text-foreground truncate">{s.label}</p>
            <span className={cn("text-[9.5px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide", verdictBadge)}>
              {verdictLabel}
            </span>
          </div>
          <p className="text-[10.5px] text-muted-foreground mt-0.5 truncate">{s.sub}</p>
        </div>
        <Button
          size="sm"
          variant={s.verdict === "valid" ? "default" : "outline"}
          onClick={onApply}
          disabled={disabled}
          className="text-[11px] h-7 shrink-0"
        >
          {s.verdict === "blocked" ? "Blocked" : <><Check className="h-3 w-3 mr-1" /> Apply</>}
        </Button>
      </div>

      {/* Impact preview row */}
      <div className="flex items-center gap-3 mt-2 text-[10.5px] text-muted-foreground flex-wrap">
        <Metric
          label="Util"
          value={`${s.newUtilizationPct}%`}
          delta={s.utilDelta > 0 ? `+${s.utilDelta}` : `${s.utilDelta}`}
          deltaTone={s.newUtilizationPct > 100 ? "bad" : s.utilDelta > 0 ? "good" : "neutral"}
        />
        <Metric
          label="Travel"
          value={s.travel === "med" ? "medium" : s.travel}
          deltaTone={s.travel === "high" ? "bad" : s.travel === "med" ? "warn" : s.travel === "low" ? "good" : "neutral"}
        />
        {s.fitScore != null && (
          <Metric
            label="Fit"
            value={`${s.fitScore}/100`}
            deltaTone={s.fitScore >= 70 ? "good" : s.fitScore >= 40 ? "warn" : "bad"}
          />
        )}
        {s.verdict !== "valid" && (
          <span className="text-[10px] text-muted-foreground/80 italic">{s.headline}</span>
        )}
      </div>
    </div>
  );
}

function Metric({
  label, value, delta, deltaTone = "neutral",
}: {
  label: string;
  value: string;
  delta?: string;
  deltaTone?: "good" | "warn" | "bad" | "neutral";
}) {
  const toneCls =
    deltaTone === "good" ? "text-success" :
    deltaTone === "warn" ? "text-warning" :
    deltaTone === "bad"  ? "text-destructive" :
                           "text-foreground";
  return (
    <span className="inline-flex items-center gap-1">
      <span className="opacity-70">{label}</span>
      <span className={cn("font-medium tabular-nums capitalize", toneCls)}>{value}</span>
      {delta && (
        <span className={cn("tabular-nums text-[10px]", toneCls)}>({delta})</span>
      )}
    </span>
  );
}
